import { supabase } from '../lib/supabase'

// Acceso a Supabase del módulo Abastecimiento. Todo el stock, los gastos
// y las correcciones se hacen con RPC: React nunca inserta movimientos de
// bodega ni gastos directamente.

// PostgREST recibe los filtros .in() en la URL: con cientos de UUID se
// supera el largo máximo permitido, así que se consulta por tandas.
const TAMANIO_TANDA_IDS = 100

export async function consultarPorTandas(ids, consultar) {
  const resultados = []

  for (let inicio = 0; inicio < ids.length; inicio += TAMANIO_TANDA_IDS) {
    const tanda = ids.slice(inicio, inicio + TAMANIO_TANDA_IDS)
    const { data, error } = await consultar(tanda)

    if (error) throw error

    resultados.push(...(data || []))
  }

  return resultados
}

async function ejecutarRpc(nombre, parametros) {
  const { data, error } = await supabase.rpc(nombre, parametros)

  if (error) throw error

  return data
}

export function registrarCompraPrevia({
  empresaId,
  proveedorId,
  fecha,
  productoId,
  precioKilo,
  kilosAcordados,
  condicion,
  plazoDias,
  observaciones,
  documento,
}) {
  return ejecutarRpc('registrar_compra_previa', {
    p_empresa_id: empresaId,
    p_proveedor_id: proveedorId,
    p_fecha_compra: fecha,
    p_producto_id: productoId,
    p_precio_kilo: precioKilo,
    p_cantidad_acordada_kg: kilosAcordados,
    p_condicion_pago: condicion,
    p_plazo_dias: plazoDias,
    p_observaciones: observaciones || null,
    p_documento: documento || null,
  })
}

export function guardarRecepcion({
  empresaId,
  compraId,
  bodegaId,
  fecha,
  pesajes,
  observacionCalidad,
}) {
  return ejecutarRpc('guardar_recepcion_compra', {
    p_empresa_id: empresaId,
    p_compra_id: compraId,
    p_bodega_id: bodegaId,
    p_fecha_recepcion: fecha,
    // DIRECTA: null (sus pesajes ya existen). PREVIA: arreglo de pesajes.
    p_pesajes: pesajes && pesajes.length > 0 ? pesajes : null,
    p_observacion_calidad: observacionCalidad || null,
  })
}

export function validarRecepcion({
  empresaId,
  recepcionId,
  kilosAceptados,
  motivoRechazo,
  observacionCalidad,
}) {
  return ejecutarRpc('validar_recepcion_compra', {
    p_empresa_id: empresaId,
    p_recepcion_id: recepcionId,
    p_kg_aceptados: kilosAceptados,
    p_motivo_rechazo: motivoRechazo || null,
    p_observacion_calidad: observacionCalidad || null,
  })
}

export function corregirRecepcionValidada({
  empresaId,
  recepcionId,
  kilosNuevos,
  motivo,
}) {
  return ejecutarRpc('corregir_recepcion_validada', {
    p_empresa_id: empresaId,
    p_recepcion_id: recepcionId,
    p_kg_aceptados_nuevos: kilosNuevos,
    p_motivo: motivo,
  })
}

// Eliminación física de una compra. Solo por RPC: nunca DELETE directo ni
// borrado de movimientos desde React. El backend revalida permisos y
// dependencias.
function primerObjeto(data) {
  return Array.isArray(data) ? data[0] ?? null : data ?? null
}

export async function previsualizarEliminacionCompra({
  empresaId,
  compraId,
}) {
  const data = await ejecutarRpc('previsualizar_eliminacion_compra', {
    p_empresa_id: empresaId,
    p_compra_id: compraId,
  })

  return primerObjeto(data)
}

export async function eliminarCompraDefinitivamente({
  empresaId,
  compraId,
  codigoConfirmacion,
  motivo,
}) {
  const data = await ejecutarRpc('eliminar_compra_definitivamente', {
    p_empresa_id: empresaId,
    p_compra_id: compraId,
    p_codigo_confirmacion: codigoConfirmacion,
    p_motivo: motivo,
  })

  return primerObjeto(data)
}

export function registrarFacturaProveedor({
  empresaId,
  proveedorId,
  tipoDocumento,
  numero,
  fechaFactura,
  condicion,
  plazo,
  neto,
  exento,
  iva,
  otros,
  total,
  compraIds,
}) {
  return ejecutarRpc('registrar_factura_proveedor', {
    p_empresa_id: empresaId,
    p_proveedor_id: proveedorId,
    p_tipo_documento: tipoDocumento,
    p_numero_documento: numero,
    p_fecha_factura: fechaFactura,
    p_condicion_pago: condicion,
    p_plazo_dias: plazo,
    p_monto_neto: neto,
    p_monto_exento: exento,
    p_monto_iva: iva,
    p_otros_impuestos: otros,
    p_monto_total: total,
    p_compra_ids: compraIds,
  })
}

// Busca una factura ya registrada para el mismo documento, para
// permitir consultarla cuando la RPC informa que ya existe.
export async function buscarFacturaExistente({
  empresaId,
  proveedorId,
  tipoDocumento,
  numero,
}) {
  // numero_normalizado = upper(btrim(numero)): igual que la RPC. No se usa
  // ilike porque "_" y "%" del número actuarían como comodines.
  const { data, error } = await supabase
    .from('facturas_proveedor')
    .select('id, gasto_id, numero_documento, monto_total')
    .eq('empresa_id', empresaId)
    .eq('proveedor_id', proveedorId)
    .eq('tipo_documento', tipoDocumento)
    .eq('numero_normalizado', numero.trim().toUpperCase())
    .maybeSingle()

  if (error) throw error

  return data
}

export async function cargarRecepcionesPorCompra(empresaId, compraIds) {
  return consultarPorTandas(compraIds, (tanda) =>
    supabase
      .from('recepciones')
      .select(
        'id, compra_id, estado, bodega_id, fecha_recepcion, kg_aceptados, observacion_calidad, motivo_rechazo, validado_en'
      )
      .eq('empresa_id', empresaId)
      .in('compra_id', tanda)
  )
}

// La vista de resumen no expone el flag de compras históricas.
export async function cargarComprasHistoricas(empresaId) {
  const { data, error } = await supabase
    .from('compras')
    .select('id')
    .eq('empresa_id', empresaId)
    .eq('recepcion_automatica_habilitada', false)

  if (error) throw error

  return (data || []).map((fila) => fila.id)
}

export async function cargarFacturasPorCompra(empresaId, compraIds) {
  return consultarPorTandas(compraIds, (tanda) =>
    supabase
      .from('facturas_proveedor_compras')
      .select('compra_id, factura_id')
      .eq('empresa_id', empresaId)
      .in('compra_id', tanda)
  )
}

export async function cargarFacturas(empresaId, facturaIds) {
  return consultarPorTandas(facturaIds, (tanda) =>
    supabase
      .from('vw_facturas_proveedor_resumen')
      .select('*')
      .eq('empresa_id', empresaId)
      .in('id', tanda)
  )
}

export async function cargarAjustesRecepcion(empresaId, recepcionId) {
  const { data, error } = await supabase
    .from('recepciones_ajustes')
    .select('id, kg_antes, kg_despues, motivo, ajustado_en')
    .eq('empresa_id', empresaId)
    .eq('recepcion_id', recepcionId)
    .order('ajustado_en', { ascending: false })

  if (error) throw error

  return data || []
}

export async function cargarBodegasActivas(empresaId) {
  const { data, error } = await supabase
    .from('bodegas')
    .select('id, codigo, nombre')
    .eq('empresa_id', empresaId)
    .eq('activo', true)
    .order('nombre', { ascending: true })

  if (error) throw error

  return data || []
}

export async function cargarTiposEnvase(empresaId) {
  const { data, error } = await supabase
    .from('tipos_envase')
    .select('id, codigo, nombre, tara_kg')
    .eq('empresa_id', empresaId)
    .eq('activo', true)
    .order('nombre', { ascending: true })

  if (error) throw error

  return data || []
}

export async function cargarPesajesCompra(empresaId, compraId) {
  const { data, error } = await supabase
    .from('compras_pesajes')
    .select(
      'id, numero_pesaje, cantidad_cajas, tipo_envase_id, envase_codigo, envase_nombre, tara_unitaria_kg, peso_bruto_kg, tara_total_kg, peso_neto_kg, observacion, pallet_tara_kg'
    )
    .eq('empresa_id', empresaId)
    .eq('compra_id', compraId)
    .order('numero_pesaje', { ascending: true })

  if (error) throw error

  return data || []
}

export async function cargarDetalleAbastecimiento(empresaId, compraId) {
  const [respuestaCompra, respuestaRecepcion, respuestaVinculo, historicas] =
    await Promise.all([
      supabase
        .from('vw_abastecimientos_resumen')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('id', compraId)
        .maybeSingle(),
      supabase
        .from('recepciones')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('compra_id', compraId)
        .maybeSingle(),
      supabase
        .from('facturas_proveedor_compras')
        .select('factura_id')
        .eq('empresa_id', empresaId)
        .eq('compra_id', compraId)
        .maybeSingle(),
      cargarComprasHistoricas(empresaId),
    ])

  if (respuestaCompra.error) throw respuestaCompra.error
  if (respuestaRecepcion.error) throw respuestaRecepcion.error
  if (respuestaVinculo.error) throw respuestaVinculo.error

  const compra = respuestaCompra.data
  const recepcion = respuestaRecepcion.data

  if (!compra) return null

  let bodega = null
  let ajustes = []
  let factura = null

  if (recepcion) {
    const [respuestaBodega, listaAjustes] = await Promise.all([
      supabase
        .from('bodegas')
        .select('nombre')
        .eq('empresa_id', empresaId)
        .eq('id', recepcion.bodega_id)
        .maybeSingle(),
      cargarAjustesRecepcion(empresaId, recepcion.id),
    ])

    bodega = respuestaBodega.data
    ajustes = listaAjustes
  }

  if (respuestaVinculo.data) {
    const facturas = await cargarFacturas(empresaId, [
      respuestaVinculo.data.factura_id,
    ])

    factura = facturas[0] || null
  }

  return {
    compra: {
      ...compra,
      recepcion_automatica_habilitada: !historicas.includes(compra.id),
    },
    recepcion,
    bodega,
    ajustes,
    factura,
  }
}

// Facturas de proveedor que originaron los gastos indicados. Un gasto
// con factura asociada es "vinculado": sus importes y documento los
// protege el backend.
export async function cargarFacturasPorGasto(empresaId, gastoIds) {
  return consultarPorTandas(gastoIds, (tanda) =>
    supabase
      .from('facturas_proveedor')
      .select('id, gasto_id, numero_documento, tipo_documento')
      .eq('empresa_id', empresaId)
      .in('gasto_id', tanda)
  )
}
